import { supabase } from './supabaseClient';

export interface Country {
  id: string;
  name: string;
  iso2: string;
  iso3: string;
  image_url: string;
  created_at: string;
}

export interface WeeklyCardWeek {
  id: string;
  week_of: string;
  status: 'draft' | 'scheduled' | 'active' | 'published' | 'archived';
  publish_at: string | null;
  published_at: string | null;
  activated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WeeklyCardData {
  id: string;
  week_id: string;
  card_type: 'artist_wisdom' | 'civic_moment' | 'spark_a_thought';
  title: string | null;
  content: string;
  subtext: string | null;
  attribution: string | null;
  country_id: string | null;
  image_url: string | null;
  headline: string | null;
  story_image_url: string | null;
  event_date: string | null;
  source_name: string | null;
  source_url: string | null;
  is_draft: boolean;
  updated_at: string;
  country?: Country | null;
}

export type CardType = WeeklyCardData['card_type'];

export interface WeekWithCards {
  week: WeeklyCardWeek;
  cards: WeeklyCardData[];
}

export interface UpsertCardPayload {
  card_type: CardType;
  title?: string | null;
  content: string;
  subtext?: string | null;
  attribution?: string | null;
  country_id?: string | null;
  image_url?: string | null;
  headline?: string | null;
  story_image_url?: string | null;
  event_date?: string | null;
  source_name?: string | null;
  source_url?: string | null;
  is_draft?: boolean;
}

function getMondayOfWeek(dateString: string): string {
  const date = new Date(dateString);
  const dayOfWeek = date.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(date);
  monday.setDate(date.getDate() + diff);
  return monday.toISOString().split('T')[0];
}

function getCurrentMonday(): string {
  const today = new Date();
  return getMondayOfWeek(today.toISOString().split('T')[0]);
}

export async function getAllCountries(): Promise<Country[]> {
  const { data, error } = await supabase
    .from('countries')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching countries:', error);
    return [];
  }

  return (data as Country[]) || [];
}

export async function getCountryById(id: string): Promise<Country | null> {
  const { data, error } = await supabase
    .from('countries')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching country:', error);
    return null;
  }

  return data as Country | null;
}

export async function getWeek(weekOf: string): Promise<WeekWithCards | null> {
  const monday = getMondayOfWeek(weekOf);

  const { data: week, error: weekError } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('week_of', monday)
    .maybeSingle();

  if (weekError) {
    console.error('[weeklyCardsPlanner] Error fetching week:', weekError);
    return null;
  }

  if (!week) {
    return null;
  }

  const { data: cards, error: cardsError } = await supabase
    .from('weekly_cards')
    .select(`
      *,
      country:country_id(*)
    `)
    .eq('week_id', week.id)
    .eq('is_draft', false)
    .order('card_type', { ascending: true });

  if (cardsError) {
    console.error('[weeklyCardsPlanner] Error fetching cards:', cardsError);
    return null;
  }

  return {
    week: week as WeeklyCardWeek,
    cards: (cards as any[]).map(card => ({
      ...card,
      country: card.country || null
    })) as WeeklyCardData[],
  };
}

export async function getWeekById(weekId: string): Promise<WeekWithCards | null> {
  const { data: week, error: weekError } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('id', weekId)
    .maybeSingle();

  if (weekError) {
    console.error('[weeklyCardsPlanner] Error fetching week by ID:', weekError);
    return null;
  }

  if (!week) {
    return null;
  }

  const { data: cards, error: cardsError } = await supabase
    .from('weekly_cards')
    .select(`
      *,
      country:country_id(*)
    `)
    .eq('week_id', week.id)
    .order('card_type', { ascending: true });

  if (cardsError) {
    console.error('[weeklyCardsPlanner] Error fetching cards:', cardsError);
    return null;
  }

  return {
    week: week as WeeklyCardWeek,
    cards: (cards as any[]).map(card => ({
      ...card,
      country: card.country || null
    })) as WeeklyCardData[],
  };
}

export async function getOrCreateWeek(weekOf: string, status: WeeklyCardWeek['status'] = 'draft'): Promise<WeeklyCardWeek | null> {
  const monday = getMondayOfWeek(weekOf);

  // First, try to get existing week
  const { data: existingWeek, error: fetchError } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('week_of', monday)
    .maybeSingle();

  if (fetchError) {
    console.error('[weeklyCardsPlanner] Error fetching week:', fetchError);
    return null;
  }

  if (existingWeek) {
    console.log('[weeklyCardsPlanner] Found existing week:', existingWeek.id);
    return existingWeek as WeeklyCardWeek;
  }

  // If no existing week, create new one
  console.log('[weeklyCardsPlanner] Creating new week for:', monday);
  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .insert({
      week_of: monday,
      status,
    })
    .select()
    .single();

  if (error) {
    console.error('[weeklyCardsPlanner] Error creating week:', error);
    return null;
  }

  const week = data as WeeklyCardWeek;

  await ensureWeekHasAllCards(week.id);

  return week;
}

export async function createWeek(weekOf: string, status: WeeklyCardWeek['status'] = 'draft'): Promise<WeeklyCardWeek | null> {
  const monday = getMondayOfWeek(weekOf);

  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .insert({
      week_of: monday,
      status,
    })
    .select()
    .single();

  if (error) {
    console.error('[weeklyCardsPlanner] Error creating week:', error);
    return null;
  }

  const week = data as WeeklyCardWeek;

  await ensureWeekHasAllCards(week.id);

  return week;
}

export async function updateWeekStatus(
  weekId: string,
  status: WeeklyCardWeek['status'],
  scheduledFor?: string | null
): Promise<WeeklyCardWeek | null> {
  const updateData: any = { status };

  if (status === 'scheduled' && scheduledFor) {
    updateData.publish_at = scheduledFor;
  }

  if (status === 'published') {
    updateData.published_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .update(updateData)
    .eq('id', weekId)
    .select()
    .single();

  if (error) {
    console.error('[weeklyCardsPlanner] Error updating week status:', error);
    return null;
  }

  return data as WeeklyCardWeek;
}

export async function upsertCard(weekId: string, payload: UpsertCardPayload): Promise<WeeklyCardData | null> {
  const upsertData = {
    week_id: weekId,
    card_type: payload.card_type,
    title: payload.title ?? null,
    content: payload.content,
    subtext: payload.subtext ?? null,
    attribution: payload.attribution ?? null,
    country_id: payload.country_id ?? null,
    image_url: payload.image_url ?? null,
    headline: payload.headline ?? null,
    story_image_url: payload.story_image_url ?? null,
    event_date: payload.event_date ?? null,
    source_name: payload.source_name ?? null,
    source_url: payload.source_url ?? null,
    is_draft: payload.is_draft ?? false,
  };

  const { data, error } = await supabase
    .from('weekly_cards')
    .upsert(upsertData, {
      onConflict: 'week_id,card_type,is_draft',
    })
    .select()
    .single();

  if (error) {
    console.error('[weeklyCardsPlanner] Error upserting card:', error);
    return null;
  }

  return data as WeeklyCardData;
}

export async function upsertWeekAndCards(
  weekOf: string,
  cards: UpsertCardPayload[],
  status: WeeklyCardWeek['status'] = 'draft',
  scheduledFor?: string | null
): Promise<WeekWithCards | null> {
  const monday = getMondayOfWeek(weekOf);

  let weekData = await getWeek(monday);

  if (!weekData) {
    const newWeek = await createWeek(monday, status);
    if (!newWeek) return null;
    weekData = { week: newWeek, cards: [] };
  } else {
    const updatedWeek = await updateWeekStatus(weekData.week.id, status, scheduledFor);
    if (updatedWeek) {
      weekData.week = updatedWeek;
    }
  }

  const updatedCards: WeeklyCardData[] = [];
  for (const cardPayload of cards) {
    const card = await upsertCard(weekData.week.id, cardPayload);
    if (card) {
      updatedCards.push(card);
    }
  }

  return {
    week: weekData.week,
    cards: updatedCards,
  };
}

async function ensureWeekHasAllCards(weekId: string): Promise<void> {
  const { data: existingCards } = await supabase
    .from('weekly_cards')
    .select('card_type')
    .eq('week_id', weekId);

  const existingTypes = new Set((existingCards || []).map((c: any) => c.card_type));
  const requiredTypes: CardType[] = ['artist_wisdom', 'civic_moment', 'spark_a_thought'];

  const missingTypes = requiredTypes.filter(type => !existingTypes.has(type));

  if (missingTypes.length === 0) return;

  const placeholders = missingTypes.map(cardType => {
    if (cardType === 'artist_wisdom') {
      return {
        week_id: weekId,
        card_type: cardType,
        content: 'Artist wisdom quote goes here',
        attribution: 'Artist Name',
      };
    } else if (cardType === 'civic_moment') {
      return {
        week_id: weekId,
        card_type: cardType,
        title: 'Civic Moment Title',
        content: 'Civic moment story goes here',
      };
    } else {
      return {
        week_id: weekId,
        card_type: cardType,
        content: 'What question should we ponder this week?',
      };
    }
  });

  const { error } = await supabase
    .from('weekly_cards')
    .insert(placeholders);

  if (error) {
    console.error('[weeklyCardsPlanner] Error creating placeholder cards:', error);
  }
}

export async function listUpcomingWeeks(): Promise<WeeklyCardWeek[]> {
  const currentMonday = getCurrentMonday();

  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .gte('week_of', currentMonday)
    .in('status', ['draft', 'scheduled'])
    .order('week_of', { ascending: true });

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching upcoming weeks:', error);
    return [];
  }

  return (data as WeeklyCardWeek[]) || [];
}

export async function listArchivedWeeks(): Promise<WeeklyCardWeek[]> {
  const currentMonday = getCurrentMonday();

  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .or(`status.eq.archived,and(status.eq.published,week_of.lt.${currentMonday})`)
    .order('week_of', { ascending: false })
    .limit(50);

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching archived weeks:', error);
    return [];
  }

  return (data as WeeklyCardWeek[]) || [];
}

export async function getCurrentWeekForApp(): Promise<WeekWithCards | null> {
  return getActiveEdition();
}

export async function getActiveEdition(): Promise<WeekWithCards | null> {
  const { data: activeWeek, error } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('status', 'active')
    .maybeSingle();

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching active edition:', error);
    return null;
  }

  if (!activeWeek) {
    return null;
  }

  const { data: cards, error: cardsError } = await supabase
    .from('weekly_cards')
    .select(`
      *,
      country:country_id(*)
    `)
    .eq('week_id', activeWeek.id)
    .eq('is_draft', false)
    .order('card_type', { ascending: true });

  if (cardsError) {
    console.error('[weeklyCardsPlanner] Error fetching active edition cards:', cardsError);
    return null;
  }

  return {
    week: activeWeek as WeeklyCardWeek,
    cards: (cards as any[]).map(card => ({
      ...card,
      country: card.country || null
    })) as WeeklyCardData[],
  };
}

export async function getCurrentLiveEdition(): Promise<WeekWithCards | null> {
  return getActiveEdition();
}

export async function getWeekWithCards(weekId: string): Promise<WeekWithCards | null> {
  const { data: week, error } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('id', weekId)
    .maybeSingle();

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching week:', error);
    return null;
  }

  if (!week) {
    return null;
  }

  const { data: cards, error: cardsError } = await supabase
    .from('weekly_cards')
    .select(`
      *,
      country:country_id(*)
    `)
    .eq('week_id', week.id)
    .eq('is_draft', false)
    .order('card_type', { ascending: true });

  if (cardsError) {
    console.error('[weeklyCardsPlanner] Error fetching week cards:', cardsError);
    return null;
  }

  return {
    week: week as WeeklyCardWeek,
    cards: (cards as any[]).map(card => ({
      ...card,
      country: card.country || null
    })) as WeeklyCardData[],
  };
}

export async function getLiveEditionDraftCards(weekId: string): Promise<WeeklyCardData[]> {
  const { data: cards, error } = await supabase
    .from('weekly_cards')
    .select(`
      *,
      country:country_id(*)
    `)
    .eq('week_id', weekId)
    .eq('is_draft', true)
    .order('card_type', { ascending: true });

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching draft cards:', error);
    return [];
  }

  return (cards as any[]).map(card => ({
    ...card,
    country: card.country || null
  })) as WeeklyCardData[];
}

export async function publishDraftCards(weekId: string): Promise<boolean> {
  const draftCards = await getLiveEditionDraftCards(weekId);

  if (draftCards.length === 0) {
    return false;
  }

  for (const draftCard of draftCards) {
    const payload: UpsertCardPayload = {
      card_type: draftCard.card_type,
      title: draftCard.title,
      content: draftCard.content,
      subtext: draftCard.subtext,
      attribution: draftCard.attribution,
      country_id: draftCard.country_id,
      image_url: draftCard.image_url,
      is_draft: false,
    };

    await upsertCard(weekId, payload);
  }

  return true;
}

export async function listEditionsByStatus(status: WeeklyCardWeek['status']): Promise<WeeklyCardWeek[]> {
  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('status', status)
    .order('week_of', { ascending: false });

  if (error) {
    console.error('[weeklyCardsPlanner] Error listing editions:', error);
    return [];
  }

  return (data as WeeklyCardWeek[]) || [];
}

export async function publishWeekNow(weekId: string): Promise<WeeklyCardWeek | null> {
  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .update({
      status: 'published',
      published_at: new Date().toISOString(),
    })
    .eq('id', weekId)
    .select()
    .single();

  if (error) {
    console.error('[weeklyCardsPlanner] Error publishing week:', error);
    return null;
  }

  return data as WeeklyCardWeek;
}

export async function scheduleWeek(weekId: string, scheduledFor: string): Promise<WeeklyCardWeek | null> {
  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .update({
      status: 'scheduled',
      publish_at: scheduledFor,
    })
    .eq('id', weekId)
    .select()
    .single();

  if (error) {
    console.error('[weeklyCardsPlanner] Error scheduling week:', error);
    return null;
  }

  return data as WeeklyCardWeek;
}

export async function archiveWeek(weekId: string): Promise<WeeklyCardWeek | null> {
  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .update({
      status: 'archived',
    })
    .eq('id', weekId)
    .select()
    .single();

  if (error) {
    console.error('[weeklyCardsPlanner] Error archiving week:', error);
    return null;
  }

  return data as WeeklyCardWeek;
}

export async function unarchiveWeek(weekId: string): Promise<WeeklyCardWeek | null> {
  const { data: week, error: fetchError } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('id', weekId)
    .maybeSingle();

  if (fetchError || !week) {
    console.error('[weeklyCardsPlanner] Error fetching week to unarchive:', fetchError);
    return null;
  }

  const newStatus = week.published_at ? 'published' : 'draft';

  const { data, error } = await supabase
    .from('weekly_card_weeks')
    .update({
      status: newStatus,
    })
    .eq('id', weekId)
    .select()
    .single();

  if (error) {
    console.error('[weeklyCardsPlanner] Error unarchiving week:', error);
    return null;
  }

  return data as WeeklyCardWeek;
}

export async function deleteWeek(weekId: string): Promise<boolean> {
  const { error } = await supabase
    .from('weekly_card_weeks')
    .delete()
    .eq('id', weekId);

  if (error) {
    console.error('[weeklyCardsPlanner] Error deleting week:', error);
    return false;
  }

  return true;
}

export async function promoteEditionToActive(editionId: string): Promise<boolean> {
  const { error } = await supabase.rpc('promote_edition_to_active', {
    target_edition_id: editionId
  });

  if (error) {
    console.error('[weeklyCardsPlanner] Error promoting edition to active:', error);
    return false;
  }

  return true;
}

export async function publishNow(editionId: string): Promise<boolean> {
  const { error: updateError } = await supabase
    .from('weekly_card_weeks')
    .update({ publish_at: new Date().toISOString() })
    .eq('id', editionId);

  if (updateError) {
    console.error('[weeklyCardsPlanner] Error setting publish time:', updateError);
    return false;
  }

  return await promoteEditionToActive(editionId);
}

export async function checkAndActivateScheduledEditions(): Promise<void> {
  const now = new Date().toISOString();

  const { data: scheduledEditions, error: fetchError } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('status', 'scheduled')
    .lte('publish_at', now)
    .order('publish_at', { ascending: true });

  if (fetchError || !scheduledEditions || scheduledEditions.length === 0) {
    return;
  }

  for (const edition of scheduledEditions) {
    await promoteEditionToActive(edition.id);
  }
}

export async function uploadWeeklyCardImage(
  editionId: string,
  cardType: CardType,
  file: File
): Promise<string | null> {
  const fileExt = file.name.split('.').pop() || 'webp';
  const prefix = cardType === 'artist_wisdom'
    ? 'artist'
    : cardType === 'civic_moment'
    ? 'civic-moment'
    : 'spark';
  const fileName = `${editionId}/${prefix}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('weekly-card-images')
    .upload(fileName, file, { upsert: true });

  if (uploadError) {
    console.error('[weeklyCardsPlanner] Error uploading image:', uploadError);
    return null;
  }

  const { data: { publicUrl } } = supabase.storage
    .from('weekly-card-images')
    .getPublicUrl(fileName);

  const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;
  return cacheBustedUrl;
}

export async function deleteWeeklyCardImage(
  editionId: string,
  cardType: CardType
): Promise<boolean> {
  const { data: files, error: listError } = await supabase.storage
    .from('weekly-card-images')
    .list(editionId);

  if (listError || !files) {
    console.error('[weeklyCardsPlanner] Error listing files:', listError);
    return false;
  }

  const matchingFiles = files.filter(file => file.name.startsWith('artist'));

  if (matchingFiles.length === 0) {
    return true;
  }

  const filesToDelete = matchingFiles.map(file => `${editionId}/${file.name}`);

  const { error: deleteError } = await supabase.storage
    .from('weekly-card-images')
    .remove(filesToDelete);

  if (deleteError) {
    console.error('[weeklyCardsPlanner] Error deleting files:', deleteError);
    return false;
  }

  return true;
}

export async function uploadCountryImage(
  countryId: string,
  file: File
): Promise<string | null> {
  const country = await getCountryById(countryId);
  if (!country) {
    console.error('[weeklyCardsPlanner] Country not found');
    return null;
  }

  const fileExt = file.name.split('.').pop() || 'png';
  const fileName = `${country.iso2.toLowerCase()}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('countries')
    .upload(fileName, file, { upsert: true });

  if (uploadError) {
    console.error('[weeklyCardsPlanner] Error uploading country image:', uploadError);
    return null;
  }

  const { data: { publicUrl } } = supabase.storage
    .from('countries')
    .getPublicUrl(fileName);

  const { error: updateError } = await supabase
    .from('countries')
    .update({ image_url: publicUrl })
    .eq('id', countryId);

  if (updateError) {
    console.error('[weeklyCardsPlanner] Error updating country image_url:', updateError);
    return null;
  }

  const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;
  return cacheBustedUrl;
}

export async function removeCountryImage(countryId: string): Promise<boolean> {
  const country = await getCountryById(countryId);
  if (!country) {
    console.error('[weeklyCardsPlanner] Country not found');
    return false;
  }

  const { data: files, error: listError } = await supabase.storage
    .from('countries')
    .list('');

  if (listError) {
    console.error('[weeklyCardsPlanner] Error listing country images:', listError);
    return false;
  }

  const matchingFiles = files?.filter(file =>
    file.name.startsWith(country.iso2.toLowerCase())
  ) || [];

  if (matchingFiles.length > 0) {
    const filesToDelete = matchingFiles.map(file => file.name);

    const { error: deleteError } = await supabase.storage
      .from('countries')
      .remove(filesToDelete);

    if (deleteError) {
      console.error('[weeklyCardsPlanner] Error deleting country images:', deleteError);
    }
  }

  const { error: updateError } = await supabase
    .from('countries')
    .update({ image_url: '' })
    .eq('id', countryId);

  if (updateError) {
    console.error('[weeklyCardsPlanner] Error clearing country image_url:', updateError);
    return false;
  }

  return true;
}

export interface PublishedHistoryItem {
  week: WeeklyCardWeek;
  cardsConfigured: number;
}

export async function getLatestPublishedEdition(): Promise<WeekWithCards | null> {
  const { data: publishedWeek, error } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('status', 'published')
    .order('published_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching latest published edition:', error);
    return null;
  }

  if (!publishedWeek) {
    return null;
  }

  const { data: cards, error: cardsError } = await supabase
    .from('weekly_cards')
    .select(`
      *,
      country:country_id(*)
    `)
    .eq('week_id', publishedWeek.id)
    .eq('is_draft', false)
    .order('card_type', { ascending: true });

  if (cardsError) {
    console.error('[weeklyCardsPlanner] Error fetching published edition cards:', cardsError);
    return null;
  }

  return {
    week: publishedWeek as WeeklyCardWeek,
    cards: (cards as any[]).map(card => ({
      ...card,
      country: card.country || null
    })) as WeeklyCardData[],
  };
}

export async function getNextScheduledEdition(): Promise<WeekWithCards | null> {
  const { data: scheduledWeek, error } = await supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('status', 'scheduled')
    .order('publish_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching next scheduled edition:', error);
    return null;
  }

  if (!scheduledWeek) {
    return null;
  }

  const { data: cards, error: cardsError } = await supabase
    .from('weekly_cards')
    .select(`
      *,
      country:country_id(*)
    `)
    .eq('week_id', scheduledWeek.id)
    .eq('is_draft', false)
    .order('card_type', { ascending: true });

  if (cardsError) {
    console.error('[weeklyCardsPlanner] Error fetching scheduled edition cards:', cardsError);
    return null;
  }

  return {
    week: scheduledWeek as WeeklyCardWeek,
    cards: (cards as any[]).map(card => ({
      ...card,
      country: card.country || null
    })) as WeeklyCardData[],
  };
}

export async function getPublishedHistory(excludeWeekId?: string): Promise<PublishedHistoryItem[]> {
  let query = supabase
    .from('weekly_card_weeks')
    .select('*')
    .eq('status', 'published')
    .order('published_at', { ascending: false });

  if (excludeWeekId) {
    query = query.neq('id', excludeWeekId);
  }

  const { data: weeks, error } = await query;

  if (error) {
    console.error('[weeklyCardsPlanner] Error fetching published history:', error);
    return [];
  }

  if (!weeks || weeks.length === 0) {
    return [];
  }

  const historyItems: PublishedHistoryItem[] = [];

  for (const week of weeks) {
    const { data: cards, error: cardsError } = await supabase
      .from('weekly_cards')
      .select('id')
      .eq('week_id', week.id)
      .eq('is_draft', false);

    if (cardsError) {
      console.error('[weeklyCardsPlanner] Error fetching cards count:', cardsError);
      historyItems.push({
        week: week as WeeklyCardWeek,
        cardsConfigured: 0,
      });
    } else {
      historyItems.push({
        week: week as WeeklyCardWeek,
        cardsConfigured: (cards || []).length,
      });
    }
  }

  return historyItems;
}
